import { Box, Button, Typography } from "@mui/material";
import "./styles.css";
import "katex/dist/katex.min.css";
import Latex from "react-latex-next";
import { useEffect, useState } from "react";
import NextAction from "../../utils/NextAction";

export default function Controls({ controls }) {
  const {
    a1,
    a2,
    a3,
    seta1,
    seta2,
    seta3,
    b1,
    b2,
    b3,
    setb1,
    setb2,
    setb3,
    c1,
    c2,
    c3,
    setc1,
    setc2,
    setc3,
    err,
  } = controls;

  return (
    <div className="controls">
      <Typography variant="h5" style={{ paddingBottom: 20 }}>
        Rotation
      </Typography>
      <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
        This is the visualization of rotation matrix. The dark axes "O'-xyz'"
        are the rotated frame axes and the light one is the reference frame
        "O-xyz".
      </Typography>
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        The rotation matrix can be represented as
      </Typography>
      <Latex>
        {` $$R = \\left[ \\begin{array}
          {ccc} a_1 & b_1 & c_1 \\\\ a_2 & b_2 & c_2 \\\\ a_3 & b_3 & c_3 \\\\ \\end{array}
          \\right] $$`}
      </Latex>
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        But there are some annoying constraints. <br /> 1. Each column vector is
        unit vector.
        <Latex>
          {` $$ a_1^2 + a_2^2 + a_3^2 = 1 $$`}
          {` $$ b_1^2 + b_2^2 + b_3^2 = 1 $$`}
          {` $$ c_1^2 + c_2^2 + c_3^2 = 1 $$`}
        </Latex>
        2. Any two column vector are orthogonal.
        <Latex>
          {` $$ a_1b_1 + a_2b_2 + a_3b_3 = 0 $$`}
          {` $$ a_1c_1 + a_2c_2 + a_3c_3 = 0 $$`}
          {` $$ b_1c_1 + b_2c_2 + b_3c_3 = 0 $$`}
        </Latex>
      </Typography>
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        Here are these 12 variables. Feel free to change the value.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <div className="controlGroup">
            <div className="control">
              <Latex>$a_1$</Latex>
              <input
                value={a1}
                onChange={(e) => seta1(e.target.value)}
                step={1 / 32}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$a_2$</Latex>
              <input
                value={a2}
                onChange={(e) => seta2(e.target.value)}
                step={1 / 32}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$a_3$</Latex>
              <input
                value={a3}
                onChange={(e) => seta3(e.target.value)}
                step={1 / 32}
                type="number"
              />
            </div>
          </div>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <div className="controlGroup">
            <div className="control">
              <Latex>$b_1$</Latex>
              <input
                value={b1}
                onChange={(e) => setb1(e.target.value)}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$b_2$</Latex>
              <input
                value={b2}
                onChange={(e) => setb2(e.target.value)}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$b_3$</Latex>
              <input
                value={b3}
                onChange={(e) => setb3(e.target.value)}
                step={1}
                type="number"
              />
            </div>
          </div>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <div className="controlGroup">
            <div className="control">
              <Latex>$c_1$</Latex>
              <input
                value={c1}
                onChange={(e) => setc1(e.target.value)}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$c_2$</Latex>
              <input
                value={c2}
                onChange={(e) => setc2(e.target.value)}
                step={1}
                type="number"
              />
            </div>
            <div className="control">
              <Latex>$c_3$</Latex>
              <input
                value={c3}
                onChange={(e) => setc3(e.target.value)}
                step={1}
                type="number"
              />
            </div>
          </div>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          alignContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            seta1(0.707);
            seta2(0.707);
            seta3(0);
            setb1(-0.707);
            setb2(0.707);
            setb3(0);
            setc1(0);
            setc2(0);
            setc3(1);
          }}
        >
          Reset
        </Button>
      </Box>
      {err && (
        <Typography
          style={{ paddingBottom: 10, textAlign: "left", color: "red" }}
        >
          Constraits Fail!
        </Typography>
      )}
      <Typography style={{ paddingBottom: 20, textAlign: "left" }}>
        You will find it is so hard to meet the constraits. We will solve this
        problem in Euler Angles.
      </Typography>

      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        Here are some example rotation matrices which you can try.
      </Typography>
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        Rotation by an angle <Latex>$\alpha$</Latex> about Z axis. Default value
        is this example with <Latex>$\alpha$</Latex> = 45 degree.
        <Latex>
          {` $$R_z(\\alpha) = \\left[ \\begin{array}
          {ccc} cos\\ \\alpha & -sin\\ \\alpha & 0 \\\\ sin\\ \\alpha & cos\\ \\alpha & 0 \\\\ 0 & 0 & 1 \\\\ \\end{array}
          \\right] $$`}
        </Latex>
      </Typography>
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        Rotation by an angle <Latex>$\beta$</Latex> about Y axis.
        <Latex>
          {` $$R_y(\\beta) = \\left[ \\begin{array}
          {ccc} cos\\ \\beta & 0 & sin\\ \\beta \\\\ 0 & 1 & 0 \\\\ -sin\\ \\beta & 0 & cos\\ \\beta \\\\ \\end{array}
          \\right] $$`}
        </Latex>
      </Typography>
      <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
        Rotation by an angle <Latex>$\gamma$</Latex> about X axis.
        <Latex>
          {` $$R_x(\\gamma) = \\left[ \\begin{array}
          {ccc} 1 & 0 & 0 \\\\ 0 & cos\\ \\gamma & -sin\\ \\gamma \\\\ 0 & sin\\ \\gamma & cos\\ \\gamma \\\\ \\end{array}
          \\right] $$`}
        </Latex>
      </Typography>
      <NextAction label={"Next: Euler Angles"} to="/eulerangles" />
    </div>
  );
}
